import { useCallback, useEffect, useRef, useState } from 'react';

export type ResizablePaneOptions = {
  /** localStorage key, so a chosen width survives navigation and reloads. */
  storageKey: string;
  defaultWidth: number;
  minWidth?: number;
  /** Upper bound before the viewport clamp is applied. */
  maxWidth?: number;
  /** Space left for the rest of the page when clamping against the viewport. */
  reservedWidth?: number;
  /** Pane edge the handle sits on. Right-hand panes grow as you drag left. */
  edge?: 'left' | 'right';
};

export type ResizablePane = {
  width: number;
  isResizing: boolean;
  /** Spread onto the drag handle element. */
  separatorProps: {
    role: 'separator';
    tabIndex: 0;
    'aria-orientation': 'vertical';
    'aria-valuenow': number;
    'aria-valuemin': number;
    'aria-valuemax': number;
    'aria-label': string;
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
    onDoubleClick: () => void;
  };
};

const readStoredWidth = (key: string): number | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Private mode or blocked site data: fall back to the default width.
    return null;
  }
};

const writeStoredWidth = (key: string, value: number) => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Nothing to do; the width simply will not persist.
  }
};

const KEYBOARD_STEP = 16;
const KEYBOARD_STEP_LARGE = 64;

/**
 * Drag-to-resize for a side pane.
 *
 * Uses pointer capture rather than window-level mousemove listeners so the drag
 * keeps tracking when the cursor leaves the handle, and cleans itself up if the
 * gesture is cancelled.
 */
export const useResizablePane = ({
  storageKey,
  defaultWidth,
  minWidth = 280,
  maxWidth = 900,
  reservedWidth = 360,
  edge = 'left',
}: ResizablePaneOptions): ResizablePane => {
  const getMaxWidth = useCallback(() => {
    const viewportMax =
      typeof window === 'undefined' ? maxWidth : window.innerWidth - reservedWidth;
    return Math.max(minWidth, Math.min(maxWidth, viewportMax));
  }, [maxWidth, minWidth, reservedWidth]);

  const clamp = useCallback(
    (value: number) => Math.round(Math.min(Math.max(value, minWidth), getMaxWidth())),
    [getMaxWidth, minWidth]
  );

  const [width, setWidth] = useState<number>(() =>
    clamp(readStoredWidth(storageKey) ?? defaultWidth)
  );
  const [isResizing, setIsResizing] = useState(false);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // Keep the pane inside the viewport when the window shrinks.
  useEffect(() => {
    const handleWindowResize = () => setWidth((current) => clamp(current));
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [clamp]);

  // Persist once the gesture settles, not on every pointer move.
  useEffect(() => {
    if (isResizing) return;
    writeStoredWidth(storageKey, width);
  }, [isResizing, width, storageKey]);

  // Suppress text selection for the duration of the drag.
  useEffect(() => {
    if (!isResizing) return;
    const previous = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.userSelect = previous;
    };
  }, [isResizing]);

  const endDrag = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setIsResizing(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Capture may already have been released; nothing to do.
    }
  }, []);

  const separatorProps: ResizablePane['separatorProps'] = {
    role: 'separator',
    tabIndex: 0,
    'aria-orientation': 'vertical',
    'aria-valuenow': width,
    'aria-valuemin': minWidth,
    'aria-valuemax': getMaxWidth(),
    'aria-label': 'Resize panel',
    onPointerDown: (e) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = { startX: e.clientX, startWidth: width };
      setIsResizing(true);
    },
    onPointerMove: (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      const delta = e.clientX - drag.startX;
      setWidth(clamp(edge === 'left' ? drag.startWidth - delta : drag.startWidth + delta));
    },
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onKeyDown: (e) => {
      const step = e.shiftKey ? KEYBOARD_STEP_LARGE : KEYBOARD_STEP;
      const grow = edge === 'left' ? 'ArrowLeft' : 'ArrowRight';
      const shrink = edge === 'left' ? 'ArrowRight' : 'ArrowLeft';

      if (e.key === grow) {
        e.preventDefault();
        setWidth((current) => clamp(current + step));
      } else if (e.key === shrink) {
        e.preventDefault();
        setWidth((current) => clamp(current - step));
      } else if (e.key === 'Home') {
        e.preventDefault();
        setWidth(clamp(defaultWidth));
      }
    },
    onDoubleClick: () => setWidth(clamp(defaultWidth)),
  };

  return { width, isResizing, separatorProps };
};
